const settings = {
  dismissKey: "kurk-geolocation-dismissed",
  sessionKey: "kurk-geolocation-checked",
  minConfidence: 0.5,
  maxChoices: 6,
};

const marketLabels = {
  gb: "United Kingdom",
  uk: "United Kingdom",
  "united-kingdom": "United Kingdom",
  usa: "United States",
  us: "United States",
  "north-amercia": "United States",
  "north-america": "United States",
  aus: "Australia",
  au: "Australia",
  australia: "Australia",
  eu: "Europe",
  europe: "Europe",
  international: "International",
};

const marketOrder = [
  "gb",
  "uk",
  "united-kingdom",
  "north-amercia",
  "north-america",
  "usa",
  "us",
  "eu",
  "europe",
  "aus",
  "au",
  "australia",
  "international",
];

const primaryCountries = ["GB", "US", "AU", "DE", "FR", "IE", "NL", "NZ", "CA"];

class GeolocationModal extends HTMLElement {
  constructor() {
    super();
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSwitch = this.onSwitch.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
    this.onMarketClick = this.onMarketClick.bind(this);
    this.onMarketKeydown = this.onMarketKeydown.bind(this);
  }

  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.start();
  }

  disconnectedCallback() {
    this.dialog?.removeEventListener("click", this.onDialogClick);
    this.dialog?.removeEventListener("close", this.onClose);
    this.switchButton?.removeEventListener("click", this.onSwitch);
    this.marketsEl?.removeEventListener("click", this.onMarketClick);
    this.marketsEl?.removeEventListener("keydown", this.onMarketKeydown);
    this.querySelectorAll("[data-geo-dismiss]").forEach((button) => {
      button.removeEventListener("click", this.onDismiss);
    });
  }

  async start() {
    try {
      if (window.Shopify?.designMode) return;

      this.dialog = this.querySelector("dialog");
      this.switchButton = this.querySelector("[data-geo-switch]");
      this.stayButton = this.querySelector("[data-geo-stay]");
      this.marketsEl = this.querySelector("[data-geo-markets]");
      this.readConfig();

      if (!this.dialog || !this.root || this.markets.length < 2) return;

      const suggestion = await this.resolveSuggestion();
      if (!suggestion || !this.isConnected) return;

      this.suggestion = suggestion;
      this.recommended = this.marketFor(suggestion.iso);
      if (!this.recommended) return;
      if (
        !this.previewing &&
        this.recommended.handle === this.currentMarketHandle
      ) {
        return;
      }

      this.selected = this.recommended;
      this.render();
      this.bind();
      this.dialog.showModal();
      this.switchButton.focus();
    } catch (error) {
      return error;
    }
  }

  readConfig() {
    const config = JSON.parse(
      this.querySelector("[data-geo-config]")?.textContent || "{}"
    );

    this.root = config.root || "/";
    this.currentCountry = config.country || "";
    this.currentCountryName = config.countryName || "";
    this.language = config.language || "en";
    this.currentMarketHandle = config.market || "";
    this.languages = Array.isArray(config.languages) ? config.languages : [];
    this.copy = config.copy || {};
    this.countries = Array.isArray(config.countries) ? config.countries : [];
    this.markets = this.buildMarkets(this.countries);
    this.currentMarket =
      this.markets.find((market) => market.handle === this.currentMarketHandle) ||
      null;
  }

  buildMarkets(countries) {
    const grouped = new Map();

    countries.forEach((country) => {
      if (!country?.market || !country.iso) return;
      if (!grouped.has(country.market)) {
        grouped.set(country.market, []);
      }
      grouped.get(country.market).push(country);
    });

    return [...grouped.entries()].map(([handle, members]) => {
      const named = [...members].sort((a, b) => {
        const aRank = primaryCountries.indexOf(a.iso);
        const bRank = primaryCountries.indexOf(b.iso);
        return (aRank === -1 ? 100 : aRank) - (bRank === -1 ? 100 : bRank);
      });
      const primary = named[0];
      let label = primary.name;

      if (members.length === 2) {
        label = `${named[0].name} & ${named[1].name}`;
      } else if (members.length > 2) {
        label = marketLabels[handle] || "International";
      }

      return {
        handle,
        label,
        currency: primary.currency,
        flag: primary.flag,
        primaryIso: primary.iso,
        isos: members.map((country) => country.iso),
      };
    });
  }

  async resolveSuggestion() {
    const preview = new URLSearchParams(window.location.search).get(
      "geolocation"
    );

    if (preview) {
      this.previewing = true;
      return this.previewSuggestion(preview);
    }

    const cached = window.sessionStorage.getItem(settings.sessionKey);
    if (cached === "none") return null;

    let suggestion = null;

    if (cached) {
      try {
        suggestion = JSON.parse(cached);
      } catch (error) {
        suggestion = null;
      }
    } else {
      suggestion = await this.fetchSuggestion();
      window.sessionStorage.setItem(
        settings.sessionKey,
        suggestion ? JSON.stringify(suggestion) : "none"
      );
    }

    if (!suggestion?.iso) return null;
    if (window.localStorage.getItem(settings.dismissKey) === suggestion.iso) {
      return null;
    }

    return suggestion;
  }

  previewSuggestion(value) {
    const code = value.toUpperCase();
    let country = this.countries.find((item) => item.iso === code);

    if (!country && (code === "1" || code === "OPEN")) {
      const other = this.markets.find(
        (market) => market.handle !== this.currentMarketHandle
      );
      country = this.countries.find((item) => item.iso === other?.primaryIso);
    }

    if (!country) return null;

    return {
      iso: country.iso,
      name: country.name,
      language: this.language,
    };
  }

  async fetchSuggestion() {
    const root = window.Shopify?.routes?.root || this.root || "/";
    const country = window.Shopify?.country || this.currentCountry;
    const language =
      window.Shopify?.language || window.Shopify?.locale || this.language;
    const endpoint = `${root.endsWith("/") ? root : `${root}/`}browsing_context_suggestions.json`;
    const response = await fetch(
      endpoint +
      "?country[enabled]=true" +
      `&country[exclude]=${encodeURIComponent(country)}` +
      "&language[enabled]=true" +
      `&language[exclude]=${encodeURIComponent(language)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const part = data.suggestions?.[0]?.parts?.country;
    if (!part?.handle || part.handle === this.currentCountry) return null;
    if (
      typeof part.confidence === "number" &&
      part.confidence < settings.minConfidence
    ) {
      return null;
    }

    const languagePart = data.suggestions[0].parts.language;

    return {
      iso: part.handle,
      name: part.name || part.handle,
      language: languagePart?.handle || this.language,
    };
  }

  marketFor(iso) {
    return this.markets.find((market) => market.isos.includes(iso)) || null;
  }

  orderedMarkets() {
    const recommended = this.recommended.handle;

    return [...this.markets].sort((a, b) => {
      if (a.handle === recommended) return -1;
      if (b.handle === recommended) return 1;
      const aRank = marketOrder.indexOf(a.handle);
      const bRank = marketOrder.indexOf(b.handle);
      return (aRank === -1 ? 100 : aRank) - (bRank === -1 ? 100 : bRank);
    });
  }

  render() {
    const countryName = this.withArticle(this.suggestion.name);
    const currentLabel = this.currentCountryName;

    this.querySelector("[data-geo-eyebrow]").textContent = this.copy.eyebrow;
    this.querySelector("[data-geo-title]").textContent = this.fill(
      this.copy.title,
      { __COUNTRY__: countryName }
    );
    this.querySelector("[data-geo-body]").textContent = this.fill(
      this.copy.bodySame,
      {
        __CURRENT__: currentLabel,
        __RECOMMENDED__: this.suggestion.name,
        __CURRENCY__: this.currencyFor(this.recommended),
      }
    );

    this.renderMarkets();
    this.updateActions();
  }

  choiceMarkets() {
    const labeled = new Set(Object.keys(marketLabels));
    const ordered = this.orderedMarkets();
    const preferred = ordered.filter(
      (market) =>
        market.handle === this.recommended.handle ||
        market.handle === this.currentMarketHandle ||
        labeled.has(market.handle)
    );
    const choices = preferred.slice(0, settings.maxChoices);
    return choices;
  }

  renderMarkets() {
    const markets = this.choiceMarkets();
    this.marketsEl.replaceChildren();
    this.marketsEl.hidden = markets.length < 2;

    markets.forEach((market) => {
      const selected = market.handle === this.selected.handle;
      const suggested = market.handle === this.recommended.handle;
      const current = market.handle === this.currentMarketHandle;
      const button = this.querySelector("[data-geo-option]").content
        .firstElementChild.cloneNode(true);

      button.setAttribute("aria-checked", selected ? "true" : "false");
      button.dataset.market = market.handle;
      if (suggested) button.dataset.suggested = "";
      if (selected) button.dataset.selected = "";

      const flag = button.querySelector("img");
      flag.src = suggested ? this.flagFor(market) : market.flag;

      button.querySelector("[data-geo-name]").textContent = market.label;
      const currency = this.currencyFor(market);
      button.querySelector("[data-geo-meta]").textContent =
        suggested && this.suggestion.name !== market.label
          ? `${this.suggestion.name} · ${currency}`
          : this.currenciesFor(market);

      const badge = button.querySelector("[data-geo-badge]");
      if (suggested || current) {
        if (current && !suggested) {
          badge.dataset.current = "";
          badge.textContent = this.copy.current;
        } else {
          badge.textContent = this.copy.suggested;
        }
      } else {
        badge.remove();
      }

      this.marketsEl.append(button);
    });
  }

  updateActions() {
    const staying = this.selected.handle === this.currentMarketHandle;
    const destination =
      this.selected.handle === this.recommended.handle
        ? this.suggestion.name
        : this.selected.label;

    this.switchButton.textContent = this.fill(
      staying ? this.copy.stay : this.copy.shop,
      {
        __MARKET__: staying ? this.currentCountryName : destination,
        __CURRENCY__: this.currencyFor(this.selected),
      }
    );
    this.stayButton.textContent = this.fill(this.copy.stay, {
      __MARKET__: this.currentCountryName,
    });
    this.stayButton.hidden = staying;
  }

  bind() {
    this.dialog.addEventListener("click", this.onDialogClick);
    this.dialog.addEventListener("close", this.onClose);
    this.switchButton.addEventListener("click", this.onSwitch);
    this.marketsEl.addEventListener("click", this.onMarketClick);
    this.marketsEl.addEventListener("keydown", this.onMarketKeydown);
    this.querySelectorAll("[data-geo-dismiss]").forEach((button) => {
      button.addEventListener("click", this.onDismiss);
    });
  }

  onMarketClick(event) {
    const button = event.target.closest("[data-market]");
    if (!button) return;
    this.switchTo(button.dataset.market);
  }

  onMarketKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    const options = [...this.marketsEl.querySelectorAll("[data-market]")];
    const index = options.findIndex((option) => option === document.activeElement);
    if (index === -1) return;

    event.preventDefault();
    const next =
      event.key === "ArrowDown"
        ? options[(index + 1) % options.length]
        : options[(index - 1 + options.length) % options.length];

    next.focus();
    this.select(next.dataset.market);
  }

  select(handle) {
    const market = this.markets.find((item) => item.handle === handle);
    if (!market) return;
    this.selected = market;
    this.renderMarkets();
    this.updateActions();
    this.marketsEl
      .querySelector(`[data-market="${CSS.escape(handle)}"]`)
      ?.focus();
  }

  onSwitch() {
    this.switchTo(this.selected.handle);
  }

  switchTo(handle) {
    if (this.switching) return;

    const market = this.markets.find((item) => item.handle === handle);
    if (!market) return;

    this.selected = market;

    if (market.handle === this.currentMarketHandle) {
      this.dismiss();
      return;
    }

    this.switching = true;
    this.submit(this.countryFor(market), this.languageFor());
  }

  onDismiss() {
    this.dismiss();
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.dismiss();
  }

  onClose() {
    if (this.switching) return;
    this.remember();
  }

  dismiss() {
    this.remember();
    if (this.dialog.open) this.dialog.close();
  }

  remember() {
    if (this.previewing || !this.suggestion?.iso) return;
    window.localStorage.setItem(settings.dismissKey, this.suggestion.iso);
  }

  submit(country, language) {
    const form = document.createElement("form");

    form.method = "POST";
    form.action = "/localization";
    form.hidden = true;

    const fields = {
      _method: "PUT",
      country_code: country,
      language_code: language,
      return_to: this.returnTo(),
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.name = name;
      input.value = value;
      form.append(input);
    });

    document.body.append(form);
    form.submit();
  }

  returnTo(href = window.location.href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete("geolocation");

    const prefix = (this.root || "/").replace(/\/$/, "");
    let path = url.pathname;

    if (
      prefix &&
      prefix !== "/" &&
      (path === prefix || path.startsWith(`${prefix}/`))
    ) {
      path = path.slice(prefix.length) || "/";
    }

    if (!path.startsWith("/")) path = `/${path}`;
    return `${path}${url.search}`;
  }

  countryFor(market) {
    if (market.isos.includes(this.suggestion.iso)) return this.suggestion.iso;
    return market.primaryIso;
  }

  languageFor() {
    const suggested = this.suggestion.language;
    if (
      this.selected.handle === this.recommended.handle &&
      this.languages.includes(suggested)
    ) {
      return suggested;
    }
    return this.language;
  }

  currencyFor(market) {
    if (market.handle === this.recommended.handle) {
      const match = this.countries.find(
        (country) => country.iso === this.suggestion.iso
      );
      if (match?.currency) return match.currency;
    }
    return market.currency;
  }

  currenciesFor(market) {
    const codes = [];
    market.isos.forEach((iso) => {
      const match = this.countries.find((country) => country.iso === iso);
      if (match?.currency && !codes.includes(match.currency)) {
        codes.push(match.currency);
      }
    });
    if (codes.length > 2) return "Local currency";
    return codes.join(" / ");
  }

  flagFor(market) {
    const match = this.countries.find(
      (country) => country.iso === this.suggestion.iso
    );
    return match?.flag || market.flag;
  }

  fill(template, values) {
    const decoded = (template || "")
      .replaceAll("&#39;", "'")
      .replaceAll("&#x27;", "'")
      .replaceAll("&amp;", "&")
      .replaceAll("&quot;", '"');

    return Object.entries(values).reduce(
      (text, [token, value]) => text.split(token).join(value || ""),
      decoded
    );
  }

  withArticle(name) {
    if (/^(United|Netherlands|Czech|Philippines|Bahamas|Dominican)/.test(name || "")) {
      return `the ${name}`;
    }
    return name;
  }
}

if (!customElements.get("geolocation-modal")) {
  customElements.define("geolocation-modal", GeolocationModal);
}
